import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// En producción, mismo origen (nginx proxifica /api); en local, el backend del .env
const BACKEND_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? process.env.REACT_APP_BACKEND_URL
  : window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Redirige al backend, que inicia el flujo OAuth de Google
    window.location.href = `${BACKEND_URL}/api/auth/google/login`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      // Limpiar storage local
      localStorage.clear();
      sessionStorage.clear();
      // Redirigir al inicio
      window.location.href = '/';
    }
  };

  // Función para cambiar de cuenta (logout + login con selector de cuenta)
  const switchAccount = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      // Redirigir a Google para seleccionar cuenta (prompt=select_account en backend)
      window.location.href = `${BACKEND_URL}/api/auth/google/login`;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    switchAccount,
    checkAuth,
    setUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
